import React, { useState, useEffect } from "react";
import "./centralAppointmentCancel.css";
import { useDispatch, useSelector } from 'react-redux';
import { fetchCentralCancelAppointmentData, updateCentralAppointmentStatus } from '../../../_services/centralServices/centralCancelAppointment.service';
import { getTokenFromLocal } from '../../../_helpers/handleResponseMiddleware';
import { toast } from 'react-toastify';
import { CentralModal } from '../CentralModal/CentralModal';
import { CentralSelect } from '../CentralSelect/CentralSelect';
import { useNavigate } from 'react-router-dom';

export const CancelModal = ({ onClose, hospitalId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Get required data from Redux store
    const reportingData = useSelector(state => state.centralReportingData);
    const userDetails = useSelector(state => state.authentication.user);
    
    const [cancelReason, setCancelReason] = useState('');
    const [primaryReason, setPrimaryReason] = useState('');
    const [secondaryReason, setSecondaryReason] = useState('');
    const [reasonsData, setReasonsData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Add state to track options
    const [primaryOptions, setPrimaryOptions] = useState([]);
    const [secondaryOptions, setSecondaryOptions] = useState([]);

    // Add a constant for special cases to ensure consistent comparison
    const SPECIAL_CASES = {
        DUPLICATE: "Duplicate",
        PT_NOT_ARRIVE: "Pt did not arrive"
    };

    const isSpecialCase = (reason) => {
        return reason === SPECIAL_CASES.DUPLICATE || reason === SPECIAL_CASES.PT_NOT_ARRIVE;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchCentralCancelAppointmentData();
                setReasonsData(response.result.data);
            } catch (error) {
                console.error('Error fetching cancellation reasons:', error);
            }
        };
        fetchData();
    }, []);

    // Add console logs to debug the data
    useEffect(() => {
        // Get the active patient data
        const activePatientData = reportingData?.reportingData?.data?.find(
            item => item?.appId === reportingData.activePatientId
        );
    }, [reportingData]);

    // Update primary options when cancelReason changes
    useEffect(() => {
        try {
            if (!cancelReason || !reasonsData) {
                setPrimaryOptions([]);
                return;
            }

            const selectedReason = reasonsData.find(reason => reason?.value === cancelReason);
            if (!selectedReason?.options) {
                setPrimaryOptions([]);
                return;
            }

            const options = selectedReason.options
                .filter(option => option && typeof option === 'object' && option.value)
                .map(option => ({
                    value: String(option.value || '').trim(),
                    label: String(option.value || '').trim()
                }))
                .filter(option => option.value !== '');

            setPrimaryOptions(options);
        } catch (error) {
            console.error('Error updating primary options:', error);
            setPrimaryOptions([]);
        }
    }, [cancelReason, reasonsData]);

    // Update secondary options when primaryReason changes
    useEffect(() => {
        try {
            if (!primaryReason || !cancelReason || !reasonsData) {
                setSecondaryOptions([]);
                return;
            }

            const selectedCancelReason = reasonsData.find(reason => reason?.value === cancelReason);
            if (!selectedCancelReason?.options) {
                setSecondaryOptions([]);
                return;
            }

            const selectedPrimary = selectedCancelReason.options.find(
                option => option?.value === primaryReason
            );

            if (!selectedPrimary?.options) {
                setSecondaryOptions([]);
                return;
            }

            const options = selectedPrimary.options
                .map(option => {
                    if (typeof option === 'string') {
                        const value = option.trim();
                        return value ? { value, label: value } : null;
                    }
                    if (option?.value) {
                        const value = String(option.value).trim();
                        return value ? { value, label: value } : null;
                    }
                    return null;
                })
                .filter(Boolean);

            setSecondaryOptions(options);
        } catch (error) {
            console.error('Error updating secondary options:', error);
            setSecondaryOptions([]);
        }
    }, [primaryReason, cancelReason, reasonsData]);

    // Updated handle change functions
    const handleCancelReasonChange = (e) => {
        try {
            const value = e?.target?.value || '';
            setCancelReason(value);
            // Only clear dependent fields if value changed
            if (value !== cancelReason) {
                setPrimaryReason('');
                setSecondaryReason('');
            }
        } catch (error) {
            console.error('Error in handleCancelReasonChange:', error);
        }
    };

    const handlePrimaryReasonChange = (e) => {
        try {
            const value = e?.target?.value || '';
            setPrimaryReason(value);
            // Only clear secondary if value changed
            if (value !== primaryReason) {
                setSecondaryReason('');
            }
        } catch (error) {
            console.error('Error in handlePrimaryReasonChange:', error);
        }
    };

    const isDataValid = () => {
        // If submitting, always return false
        if (isSubmitting) return false;

        // Special case for "Duplicate" and "PT did not arrive"
        if (isSpecialCase(cancelReason)) {
            // Only require the cancelReason for these cases
            return Boolean(cancelReason);
        }

        // For all other cases, require all fields
        return Boolean(
            cancelReason && 
            primaryReason && 
            secondaryReason
        );
    };

    // Simple close without navigation
    const handleSimpleClose = () => {
        if (typeof onClose === 'function') {
            onClose();
        }
    };

    // Close with navigation (for save button)
    const handleCloseWithNavigation = () => {
        try {
            if (typeof onClose === 'function') {
                onClose();
            }
            // Navigate after successful cancellation
            if (hospitalId) {
                navigate(`/central/${hospitalId}`);
            }
        } catch (error) {
            console.error('Error closing modal:', error);
            // Fallback navigation
            window.location.href = hospitalId ? `/central/${hospitalId}` : '/central';
        }
    };

    const handleSubmit = async () => {
        const user = userDetails || getTokenFromLocal();
        const appId = reportingData?.activePatientId;
        
        // Find the active patient data to get the leadId
        const activePatientData = reportingData?.reportingData?.data?.find(
            item => item.appointment_id === appId || item._id === appId
        );
        const leadId = activePatientData?.lead_id;

        if (!appId || !leadId || !user?.userId) {
            toast.error('Required appointment data is missing');
            console.error('Missing data:', { appId, leadId, userId: user?.userId });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                appId: appId,
                updateObj: {
                    appointmentStatus: {
                        value: "Cancelled",
                        actionTakenBy: user?.userId,
                        actionTakenByUser: user?.username || user?.name,
                        dateTimeIso: new Date().toISOString(),
                        CancellationReason: cancelReason,
                        // Only include other reasons if not a special case
                        ...(!isSpecialCase(cancelReason) && {
                            primaryCancellationReason: primaryReason || "",
                            secondaryCancellationReason: secondaryReason || "",
                            source: "hosChatApp"
                        })
                    },
                    leadId: leadId
                }
            };

            const response = await updateCentralAppointmentStatus(payload);
            if (response?.message === 'Success') {
                toast.success('Appointment cancelled successfully');
                handleCloseWithNavigation();
            } else {
                toast.error('Failed to cancel appointment');
            }
        } catch (error) {
            console.error('Error cancelling appointment:', error);
            toast.error(error?.message || 'Failed to cancel appointment');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <CentralModal
            modalTitle="Cancel Appointment"
            open={true}
            onClose={handleSimpleClose}
            buttonTitle="Save"
            cancelTitle="Cancel"
            isShowDivider={true}
            isSubmitDisabled={!isDataValid() || isSubmitting}
            onSubmit={handleSubmit}
            onCancel={handleSimpleClose}
            isModalFooterShow={true}
            modalButton={<span></span>}
        >
            <div className="equipmentContentContainer">
                {(!reportingData?.activePatientId || !reportingData?.reportingData?.data?.[0]?.leadId) && (
                    <div className="warning-message">
                        Required appointment data is missing
                    </div>
                )}
                
                <CentralSelect 
                    value={cancelReason || ''}
                    label="Cancellation Reason *"
                    options={
                        Array.isArray(reasonsData) ? 
                            reasonsData
                                .filter(reason => reason && typeof reason === 'object' && reason.value)
                                .map(reason => ({
                                    label: String(reason.value || '').trim(),
                                    value: String(reason.value || '').trim()
                                }))
                                .filter(option => option.value !== '') : 
                            []
                    }
                    onChange={handleCancelReasonChange}
                />

                {!isSpecialCase(cancelReason) && (
                    <>
                        <CentralSelect 
                            value={primaryReason || ''}
                            label="Primary Reason *"
                            options={primaryOptions}
                            onChange={handlePrimaryReasonChange}
                            disabled={!cancelReason}
                        />

                        <CentralSelect 
                            value={secondaryReason || ''}
                            label="Secondary Reason *"
                            options={secondaryOptions}
                            onChange={(e) => setSecondaryReason(e?.target?.value || '')}
                            disabled={!primaryReason}
                        />
                    </>
                )}
            </div>
        </CentralModal>
    );
};
